import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useStaff } from "@/context/StaffAuth";
import { api, errMsg } from "@/lib/api";
import { DemoBadge } from "@/components/admin/AdminUI";

export default function AdminLogin() {
  const { login, user, loading } = useStaff();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [demo, setDemo] = useState(false);

  useEffect(() => {
    document.title = "Staff sign in · Maison Éclat";
    api.get("/store/config").then((r) => setDemo(!!r.data?.demo_catalog)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!loading && user) navigate(loc.state?.from && loc.state.from !== "/admin/login" ? loc.state.from : "/admin", { replace: true });
  }, [loading, user, navigate, loc.state]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Signed in");
      navigate(loc.state?.from && loc.state.from !== "/admin/login" ? loc.state.from : "/admin", { replace: true });
    } catch (err) {
      toast.error(errMsg(err, "Incorrect email or password"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grid lg:grid-cols-12" data-testid="admin-login-page">
      <aside className="hidden lg:flex lg:col-span-5 flex-col justify-between bg-secondary border-r px-14 py-12 relative overflow-hidden grain">
        <Link to="/" className="flex items-center gap-3" data-testid="admin-login-brand-link">
          <span className="h-9 w-9 rounded-md bg-foreground text-background flex items-center justify-center font-display text-xl leading-none">É</span>
          <span className="font-display text-2xl">Maison Éclat</span>
        </Link>
        <div>
          <p className="eyebrow">Control center</p>
          <h1 className="font-display text-5xl xl:text-6xl font-medium tracking-[-0.02em] mt-4 leading-[1.05]">Merchandising, stock and clienteling in one calm place.</h1>
          <p className="text-sm text-muted-foreground mt-6 max-w-md leading-relaxed">Server-side permissions, immutable stock ledger, audited privileged actions. EUR base currency with exact decimal arithmetic.</p>
        </div>
        <p className="text-[11px] text-muted-foreground">Luxury Commerce OS · Phases 1–3</p>
      </aside>

      <main className="lg:col-span-7 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10 flex items-center gap-3">
            <span className="h-9 w-9 rounded-md bg-foreground text-background flex items-center justify-center font-display text-xl leading-none">É</span>
            <span className="font-display text-2xl">Maison Éclat</span>
          </div>
          <div className="flex items-center gap-3">
            <p className="eyebrow">Staff access</p>
            {demo && <DemoBadge />}
          </div>
          <h2 className="font-display text-4xl font-medium mt-2">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-2">Use your staff credentials. Sessions expire automatically and every privileged action is recorded.</p>

          <form onSubmit={submit} className="mt-10 space-y-5 bg-card border rounded-xl p-6 sm:p-8 shadow-[0_1px_0_hsl(var(--shadow-1))]" data-testid="admin-login-form">
            <div className="space-y-1.5">
              <Label htmlFor="staff-email">Work email</Label>
              <Input id="staff-email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-lg" data-testid="admin-login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="staff-password">Password</Label>
              <Input id="staff-password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 rounded-lg" data-testid="admin-login-password-input" />
            </div>
            <Button type="submit" disabled={busy || !email || !password} className="w-full h-11 rounded-lg text-[13px] font-semibold uppercase tracking-[0.14em]" data-testid="admin-login-submit-button">
              {busy ? "Signing in…" : <>Sign in <ArrowRight className="h-4 w-4 ml-2" /></>}
            </Button>
            <p className="flex items-center gap-2 text-[11px] text-muted-foreground"><Lock className="h-3 w-3" /> Accounts lock temporarily after repeated failed attempts.</p>
          </form>

          {demo && (
            <div className="mt-6 rounded-xl border border-dashed bg-secondary/60 p-4 text-xs text-muted-foreground" data-testid="admin-login-demo-hint">
              <p className="font-semibold text-foreground mb-1">Demo workspace</p>
              <p>Administrator: <code className="text-foreground">admin@maison-eclat.example</code></p>
              <button type="button" onClick={() => { setEmail("admin@maison-eclat.example"); setPassword("Admin#2026Lux"); }} className="mt-2 underline underline-offset-4 hover:text-foreground" data-testid="admin-login-fill-demo-button">Fill demo administrator credentials</button>
            </div>
          )}

          <p className="mt-8 text-xs text-muted-foreground"><Link to="/" className="link-lux" data-testid="admin-login-storefront-link">Return to the storefront</Link></p>
        </div>
      </main>
    </div>
  );
}
